import { UserStatus } from '@mews-commander/data-access';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { LocalizedMinorHighlight } from '@mews-commander/ui';
import { FormInputField, FormSelectField, LocalizedText, useFeatureFlag, useLocalizeEnum } from '@mews/framework';
import { Stack, StackItem, TextInput } from '@optimus-web/core';
import { useUserProfilesGet } from '../../queries';
import { CommonFormFields } from '../CommonFormFields';
import { FieldNames } from '../FieldNames';
import { LockedFormProps } from '../../types';
import { createListItems } from '../../utils';
import { useUserType } from '../../hooks/useUserType';
import { useDepartmentsOptions } from '../../hooks/useCommonFormOptions';
import { useIsUserProvisioned } from '../../hooks/useIsUserProvisioned';
import { SuperiorSearchField } from '../AddLocalUser';
import { ResendInviteButton } from '../UserDetail/ResendInviteButton';
import { LearningPathLockableField } from './CustomFields/LearningPathLockableField';
import { EmployeeIncentiveCodeLockableField } from './CustomFields/EmployeeIncentiveCodeLockableField';

export const EditUserForm = ({
    lockedFields,
    setLockedFields,
    fieldsConfig,
    userId,
}: LockedFormProps & { userId: string }) => {
    const localizeEnum = useLocalizeEnum();
    const { isPortfolioPropertyUser, isPortfolioCentralUser, isPropertyUser } = useUserType();
    const employeeIncentiveCodeEnabled = useFeatureFlag(FeatureFlag.employeeIncentiveCodeEnabled);

    const { data: detailedUser } = useUserProfilesGet(userId);

    const departmentOptions = useDepartmentsOptions();

    const isUserProvisioned = useIsUserProvisioned(detailedUser);

    return (
        <Stack vertical spacing="400">
            <Stack distribution="stretch" vertical spacing="200">
                <LocalizedMinorHighlight textKey={TextKey.Profile} />

                <Stack spacing="200" distribution="stretch">
                    <FormInputField
                        name={FieldNames.FirstName}
                        label={<LocalizedText textKey={TextKey.FirstName} />}
                        readOnly={isPortfolioCentralUser || isUserProvisioned}
                        required={!isPortfolioCentralUser}
                    />
                    <FormInputField
                        name={FieldNames.LastName}
                        label={<LocalizedText textKey={TextKey.LastName} />}
                        readOnly={isPortfolioCentralUser || isUserProvisioned}
                        required={!isPortfolioCentralUser}
                    />
                </Stack>

                <Stack spacing="50" vertical>
                    <TextInput
                        name={FieldNames.Status}
                        label={<LocalizedText textKey={TextKey.Status} />}
                        value={detailedUser ? localizeEnum('UserStatus', detailedUser.status) : ''}
                        readOnly
                    />

                    {detailedUser?.status === UserStatus.Pending &&
                    detailedUser.isVerified === false &&
                    detailedUser?.email ? (
                        <StackItem>
                            <ResendInviteButton email={detailedUser.email} />
                        </StackItem>
                    ) : null}
                </Stack>

                <LearningPathLockableField
                    fieldsConfig={fieldsConfig}
                    detailedUser={detailedUser}
                    lockedFields={lockedFields}
                    setLockedFields={setLockedFields}
                    isUserProvisioned={isUserProvisioned}
                />

                {isPropertyUser && (
                    <FormSelectField
                        name={FieldNames.DepartmentId}
                        label={<LocalizedText textKey={TextKey.Department} />}
                    >
                        {createListItems(departmentOptions)}
                    </FormSelectField>
                )}
                {!isPortfolioPropertyUser && (
                    <SuperiorSearchField
                        excludedUserId={userId}
                        preselectedProfileId={detailedUser?.superiorProfileIds?.[0]}
                    />
                )}

                {employeeIncentiveCodeEnabled ? (
                    <EmployeeIncentiveCodeLockableField
                        fieldsConfig={fieldsConfig}
                        lockedFields={lockedFields}
                        setLockedFields={setLockedFields}
                    />
                ) : null}
            </Stack>

            <CommonFormFields
                lockedFields={lockedFields}
                setLockedFields={setLockedFields}
                fieldsConfig={fieldsConfig}
                detailedUser={detailedUser}
            />
        </Stack>
    );
};
